import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './PublicNavbar.css';

const ClipboardIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
);

const LANGUAGES = [
    { code: 'it', label: 'Italiano' },
    { code: 'en', label: 'English'  },
];

const ChevronIcon = () => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="2,3 5,7 8,3" />
    </svg>
);

/**
 * PublicNavbar component.
 *
 * Top navigation bar shared across all public (unauthenticated) pages:
 * landing, get-started, login, and register. Contains:
 * - Logo + brand name (navigates to `/`)
 * - Pricing and Contacts links
 * - "Get Started" CTA button (navigates to `/get-started`)
 *
 * On screens ≤ 768 px the links and button collapse (hidden via CSS).
 *
 * @component
 * @returns {JSX.Element}
 */
const PublicNavbar = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const activeLang = (i18n.language || 'it').split('-')[0].toLowerCase();
    const current = LANGUAGES.find(l => l.code === activeLang) ?? LANGUAGES[0];

    const [open, setOpen] = useState(false);
    const langRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (langRef.current && !langRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectLanguage = (code) => {
        i18n.changeLanguage(code);
        setOpen(false);
    };

    return (
        <nav className="pnb">
            <div className="pnb-container">
                <div className="pnb-inner">

                    {/* Logo */}
                    <a className="pnb-logo" href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }}>
                        <div className="pnb-logo-icon"><ClipboardIcon /></div>
                        <span className="pnb-logo-text">Dietist<span>Pro</span></span>
                    </a>

                    {/* Links + CTA */}
                    <div className="pnb-links">

                        {/* Language selector */}
                        <div className="pnb-lang" ref={langRef}>
                            <button
                                className="pnb-lang-btn"
                                onClick={() => setOpen(o => !o)}
                                aria-haspopup="listbox"
                                aria-expanded={open}
                                aria-label={t('settings-language-section')}
                                type="button"
                            >
                                <span className="pnb-lang-code">{current.code.toUpperCase()}</span>
                                <span className={`pnb-lang-chevron${open ? ' pnb-lang-chevron--open' : ''}`}>
                                    <ChevronIcon />
                                </span>
                            </button>

                            {open && (
                                <ul className="pnb-lang-dropdown" role="listbox">
                                    {LANGUAGES.map(lang => (
                                        <li
                                            key={lang.code}
                                            className={`pnb-lang-option${lang.code === activeLang ? ' pnb-lang-option--active' : ''}`}
                                            role="option"
                                            aria-selected={lang.code === activeLang}
                                            onClick={() => selectLanguage(lang.code)}
                                        >
                                            <span className="pnb-lang-option-code">{lang.code.toUpperCase()}</span>
                                            <span className="pnb-lang-option-label">{lang.label}</span>
                                            {lang.code === activeLang && <span className="pnb-lang-check">✓</span>}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <a href="/pricing" onClick={(e) => { e.preventDefault(); navigate('/pricing'); }}>
                            {t('nlp-nav-pricing')}
                        </a>
                        <a href="/contacts" onClick={(e) => { e.preventDefault(); navigate('/contacts'); }}>
                            {t('nlp-nav-contacts')}
                        </a>
                        <button className="pnb-cta" onClick={() => navigate('/get-started')}>
                            {t('nlp-nav-get-started')}
                        </button>
                    </div>

                </div>
            </div>
        </nav>
    );
};

export default PublicNavbar;
